import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import ArrowButton from "../../../Components/Common/ArrowButton";
import { ROUTES } from "../../../Utils/constants";
import { useThemeContext } from "../../../Context/ThemeContext";
import "./MachineLevelBin.css";
import { getMachineBasicDetails } from "../../../Services/authService";
import { useTranslation } from "react-i18next";

function MachineBinLevel() {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const [machineData, setMachineData] = useState([]);
  //const [machineTypeData, setMachineTypeData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cardBackground = mode === "dark" ? "#3A3A3A" : "#F3F5F7";

  const textColor = mode === "dark" ? "#FFFFFF" : "#30383A";

  const subTextColor = mode === "dark" ? "#B8B8B8" : "#697174";

  useEffect(() => {
    const fetchMachinesData = async () => {
      try {
        setLoading(true);

        const machineResponse = await getMachineBasicDetails();

        const machines = machineResponse?.data || [];

        // console.log("Machine Basic Details:", machines);

        const returnMachines = machines.filter(
          (machine) => machine.machineType === "Return Vending Machine",
        );

        // console.log("Return Vending Machines:", returnMachines);

        setMachineData(returnMachines);
      } catch (error) {
        console.error("Error fetching machine bin level:", error);

        setMachineData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMachinesData();
  }, []);

  return (
    <Box className="machine-bin-container">
      {/* ========================================
          HEADER
      ======================================== */}

      <div className="machine-bin-header">
        <Typography className="machine-bin-heading">
          {t("dashboard.returnMachinesBinLevel")}
        </Typography>

        <ArrowButton targetRoute={ROUTES.VENDING_MACHINES} />
      </div>

      {/* ========================================
          MACHINE LIST
      ======================================== */}

      <div className="machine-bin-list">
        {/* LOADING */}
        {loading ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : machineData.length === 0 ? (
          /* NO DATA */
          <Typography
            sx={{
              textAlign: "center",
              width: "100%",
            }}
          >
            {t("dashboard.noMachineData")}
          </Typography>
        ) : (
          /* MACHINE DATA */
          machineData.map((machine) => {
            // ========================================
            // BIN LEVEL VALUES
            // ========================================

            const bowlsValue = Number(machine.bowlBinLevel) || 0;

            const bottlesValue = Number(machine.bottleBinLevel) || 0;

            return (
              <div
                key={machine.id}
                className="machine-bin-card"
                style={{
                  backgroundColor: cardBackground,
                }}
              >
                {/* ========================================
                    LEFT SIDE
                ======================================== */}

                <div className="machine-bin-details">
                  <Typography
                    className="machine-bin-location"
                    style={{
                      color: textColor,
                    }}
                  >
                    {machine.location || machine.street || "-"}
                  </Typography>

                  <Typography
                    className="machine-bin-id"
                    style={{
                      color: subTextColor,
                    }}
                  >
                    {machine.machineNumber || "-"}
                  </Typography>
                </div>

                {/* ========================================
                    RIGHT SIDE - BIN PROGRESS
                ======================================== */}

                <div className="machine-bin-progress">
                  {/* BOWLS */}

                  {/* {showBowls && ( */}
                  <BinProgress
                    value={bowlsValue}
                    label={t("returns_by_location.bowls")}
                    color="#EABB15"
                    backgroundColor={cardBackground}
                    textColor={textColor}
                    subTextColor={subTextColor}
                  />
                  {/* )} */}

                  {/* BOTTLES */}

                  {/* {showBottles && ( */}
                  <BinProgress
                    value={bottlesValue}
                    label={t("returns_by_location.bottles")}
                    color="#009E5A"
                    backgroundColor={cardBackground}
                    textColor={textColor}
                    subTextColor={subTextColor}
                  />
                  {/* )} */}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Box>
  );
}

/* ========================================
   CIRCULAR BIN PROGRESS
======================================== */


function BinProgress({
  value,
  label,
  backgroundColor,
  textColor,
  subTextColor,
}) {
  const percentage = Math.min(
    Math.max(Number(value) || 0, 0),
    100
  );

  // Determine bin status color
  const statusColor =
    percentage === 100
      ? "#FF4141" // Red: 100%
      : percentage > 75
        ? "#EABB15" // Yellow: 76%-99%
        : "#009E5A"; // Green: 0%-75%

  return (
    <div className="bin-progress-wrapper">
      <div className="bin-progress-circle">
        {/* BACKGROUND CIRCLE */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={62}
          thickness={6}
          className="bin-progress-background"
          sx={{
            color: "#D9DEE2",
          }}
        />

        {/* ACTUAL PROGRESS */}
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={62}
          thickness={6}
          className="bin-progress-value"
          sx={{
            color: statusColor,
          }}
        />

        {/* CENTER CONTENT */}
        <div
          className="bin-progress-content"
          style={{
            backgroundColor: backgroundColor,
          }}
        >
          <span
            className="bin-percentage"
            style={{
              color: textColor,
            }}
          >
            {percentage}%
          </span>

          <span
            className="bin-label"
            style={{
              color: subTextColor,
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}

export default MachineBinLevel;
